import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';
import { BrandPanel } from './Login';
import { CheckCircleIcon, ExclamationCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

type Status = 'loading' | 'success' | 'expired' | 'error';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [status, setStatus] = useState<Status>('loading');
  const [resendEmail, setResendEmail] = useState('');
  const [resendSent, setResendSent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (!token) { setStatus('error'); return; }
    apiService.verifyEmail(token)
      .then(() => setStatus('success'))
      .catch((err: any) => {
        const msg: string = err.response?.data?.message ?? '';
        setStatus(msg.includes('expiré') ? 'expired' : 'error');
      });
  }, [token]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendLoading(true);
    try {
      await apiService.resendVerification(resendEmail);
      setResendSent(true);
    } catch { /* silencieux */ }
    finally { setResendLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <BrandPanel subtitle="Vérification de votre adresse email" />

      <div className="flex-1 flex flex-col items-center justify-center bg-white px-6 py-12 sm:px-12">

        {/* Logo mobile */}
        <div className="lg:hidden flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-3 shadow-lg overflow-hidden border border-gray-100">
            <img src="/armoirie.jpeg" alt="Armoiries" className="w-full h-full object-contain p-1" />
          </div>
          <span className="text-xl font-bold text-gray-900">Suivi Démat</span>
          <span className="text-xs text-primary-600 font-semibold tracking-widest uppercase mt-0.5">MTDPCE</span>
        </div>

        <div className="w-full max-w-md text-center">

          {/* ── Chargement ── */}
          {status === 'loading' && (
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 border-4 border-primary-100 border-t-primary-600 rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Vérification de votre email en cours…</p>
            </div>
          )}

          {/* ── Succès ── */}
          {status === 'success' && (
            <>
              <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircleIcon className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Email vérifié !</h2>
              <p className="text-sm text-gray-500 mb-3 leading-relaxed">
                Votre adresse email a été confirmée avec succès.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 mb-8 text-left">
                <div className="flex items-start gap-3">
                  <ClockIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800 mb-1">En attente d'activation</p>
                    <p className="text-sm text-amber-700 leading-relaxed">
                      Votre dossier est maintenant transmis aux services compétents de la transition numérique.
                      Votre compte sera activé dans les plus brefs délais.
                    </p>
                  </div>
                </div>
              </div>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl transition-colors"
              >
                Retour à la connexion
              </Link>
            </>
          )}

          {/* ── Lien expiré ── */}
          {status === 'expired' && (
            <>
              <div className="mx-auto w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                <ClockIcon className="h-10 w-10 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Lien expiré</h2>
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                Ce lien de vérification a expiré (validité 24h). Entrez votre email pour en recevoir un nouveau.
              </p>
              {resendSent ? (
                <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-4">
                  <p className="text-sm font-semibold text-green-800 mb-1">Email renvoyé !</p>
                  <p className="text-sm text-green-700">
                    Si cet email est enregistré, un nouveau lien a été envoyé. Vérifiez votre boîte de réception.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleResend} className="space-y-3">
                  <input
                    type="email"
                    required
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="Votre adresse email"
                    className="block w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  />
                  <button
                    type="submit"
                    disabled={resendLoading}
                    className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-60"
                  >
                    {resendLoading ? 'Envoi en cours…' : 'Renvoyer le lien de vérification'}
                  </button>
                </form>
              )}
              <p className="mt-5 text-sm text-gray-400">
                <Link to="/login" className="text-primary-600 hover:underline">Retour à la connexion</Link>
              </p>
            </>
          )}

          {/* ── Erreur générique ── */}
          {status === 'error' && (
            <>
              <div className="mx-auto w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
                <ExclamationCircleIcon className="h-10 w-10 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Lien invalide</h2>
              <p className="text-sm text-gray-500 mb-8 leading-relaxed">
                Ce lien de vérification est invalide ou a déjà été utilisé.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl transition-colors"
              >
                Retour à la connexion
              </Link>
            </>
          )}

        </div>

        <p className="lg:hidden mt-10 text-center text-xs text-gray-400">© 2026 MTDPCE — Burkina Faso</p>
      </div>
    </div>
  );
};

export default VerifyEmail;
